import { ConfigProvider, Tabs, TabsProps, ThemeConfig } from 'antd';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';

import HeroSection from './HeroSection';
import ProfileForm from './ProfileForm';
import ProfileSecurity from './ProfileForm/ProfileSecurity';
import ManageAddress from '../ManageAddress';
import { user } from '../../atoms/user';
import { EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import { User } from '../../utils/interfaces/User';

const config: ThemeConfig = {
  token: {
    colorPrimary: 'white',
    fontFamily: 'inherit',
  },
};

const Profile = () => {
  const router = useRouter();
  const activeTab = parseInt(router.query.activeTab as string) || 1;
  const previousTab = useRef<number | null>(null);

  const userData: User | undefined = useRecoilValue(user);
  const startTimeRef = useRef<Date | null>(null);

  const triggerViewEvent = (tabKey: string) => {
    const eventParams: EventDetails = {
      event_name: 'screen_view',
      source: 'global',
      source_type:
        tabKey === '3' ? 'address' : tabKey === '2' ? 'password' : 'profile',
      source_id: null,
      sub_source: null,
      sub_source_id: null,
      unit: 'boolean',
      value: 1,
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const triggerSessionEvent = (tabKey: string, start: Date, end: Date) => {
    const sessionDuration = (end.getTime() - start.getTime()) / 1000;

    const eventParams: EventDetails = {
      event_name: 'screen_session',
      source: 'global',
      source_type:
        tabKey === '3' ? 'address' : tabKey === '2' ? 'password' : 'profile',
      source_id: null,
      sub_source: null,
      sub_source_id: null,
      unit: 'seconds',
      value: sessionDuration,
      activity_start: moment(start).format('YYYY-MM-DD HH:mm:ss'),
      activity_end: moment(end).format('YYYY-MM-DD HH:mm:ss'),
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
    startTimeRef.current = null;
  };

  useEffect(() => {
    const currentTime = new Date();

    if (startTimeRef.current && previousTab.current) {
      triggerSessionEvent(
        previousTab.current?.toString(),
        startTimeRef?.current,
        currentTime,
      );
    }

    triggerViewEvent(activeTab?.toString());
    previousTab.current = activeTab;
    startTimeRef.current = currentTime;
  }, [activeTab]);

  useEffect(() => {
    return () => {
      const currentTime = new Date();
      if (startTimeRef.current) {
        triggerSessionEvent(
          activeTab?.toString(),
          startTimeRef.current,
          currentTime,
        );
      }
    };
  }, [activeTab]);

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span
          className={`text-base md:text-lg ${activeTab === 1 ? 'text-white' : ''}`}
        >
          Profile Info
        </span>
      ),
      children: (
        <div className='relative overflow-hidden pt-8 md:pt-10 flex flex-col gap-y-10 pb-20'>
          <div>
            <span>
              <HeroSection />
            </span>
            <span className='pt-10'>
              <ProfileForm />
            </span>
          </div>
        </div>
      ),
    },
    {
      key: '2',
      label: (
        <span
          className={`text-base md:text-lg ${activeTab === 2 ? 'text-white' : ''}`}
        >
          Account Security
        </span>
      ),
      children: (
        <div className='relative overflow-hidden pt-8 md:pt-10 flex flex-col gap-y-10 pb-20'>
          <span>
            <ProfileSecurity />
          </span>
        </div>
      ),
    },
    {
      key: '3',
      label: (
        <span
          className={`text-base md:text-lg ${activeTab === 3 ? 'text-white' : ''}`}
        >
          Address
        </span>
      ),
      children: (
        <div className='relative flex flex-col gap-y-10 pb-20'>
          <span>
            <ManageAddress />
          </span>
        </div>
      ),
    },
  ];

  return (
    <ConfigProvider theme={config}>
      <div className='relative overflow-hidden lg:px-24 pt-24 flex flex-col md:gap-y-10 md:pb-20 md:min-h-[100vh]'>
        <div className='flex justify-center'>
          <div className='w-full md:w-2/3 px-6'>
            <Tabs
              className='customTabProfile'
              activeKey={activeTab.toString()}
              items={items}
              onChange={(key) => router.push(`/profile/?activeTab=${key}`)}
            />
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Profile;
